import { Icon, IconSize, IconVariants, Text, TextVariants, Tooltip } from "@next-platform/client-common-spicy-ui";
import { ReactNode, useEffect } from "react";

import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import { cn } from "@/utils";

export enum PageSize {
  Small = "small",
  Medium = "medium",
  Large = "large",
}

export enum PageVariant {
  Modal = "modal",
}

type PageLayoutProps = {
  children: ReactNode;
  size?: PageSize;
  variant?: PageVariant;
  className?: string;
};

export function PageLayout({ children, size = PageSize.Large, variant, className: classNameProp }: PageLayoutProps) {
  let className = cn("w-full mx-auto", classNameProp);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (size === PageSize.Small) {
    className = cn(className, "max-w-[608px]");
  } else if (size === PageSize.Medium) {
    className = cn(className, "max-w-[750px]");
  }

  if (variant === PageVariant.Modal) {
    className = cn(className, "bg-gray-1 dark:bg-gray-3 rounded-lg p-3 md:p-4 border border-solid border-gray-5 ");
  }

  return <div className={className}>{children}</div>;
}

interface PageLayoutHeaderProps {
  title: string;
  actionLabel?: string;
  actionTo?: string;
  tooltip?: string;
  subTitle?: string;
}

PageLayout.Header = function PageLayoutHeader({ title, subTitle, actionLabel, actionTo, tooltip }: PageLayoutHeaderProps) {
  return (
    <>
      <div className="flex justify-between max-mob:flex-col">
        <div className="flex items-center gap-sm whitespace-nowrap">
          <Text variant={TextVariants.HEADING_5_BOLD} className="flex gap-sm items-center">
            {title}
          </Text>
          {!!tooltip && (
            <Tooltip title={tooltip}>
              <Icon icon={IconVariants.INFO} size={IconSize.SMALL} />
            </Tooltip>
          )}
        </div>
        {actionTo && (
          <LocalizedLink className="flex items-center text-link cursor-pointer" target="_blank" to={actionTo} aria-label="chatbot-guide">
            <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>{actionLabel || ""}</Text>
          </LocalizedLink>
        )}
      </div>
      {!!subTitle && (
        <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-gray-12 pt-xl">
          {subTitle}
        </Text>
      )}
    </>
  );
};

interface PageLayoutContentProps {
  children: ReactNode;
  classNames?: string;
}

PageLayout.Content = function PageLayoutContent({ children, classNames = "" }: PageLayoutContentProps) {
  return <div className={classNames ? `py-xl ${classNames}` : "py-xl"}>{children}</div>;
};

export default PageLayout;
